// Story 6.9 (ADO-1163, AC3) — Radio Button group. Figma 689:5607.
// a11y: a role="radiogroup" labelled by its legend; each option is a native
// radio wrapped in a label sharing one `name`.

import { useId } from 'react';
import type { ReactNode } from 'react';
import styles from './Radio.module.css';

export interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface RadioGroupBaseProps {
  /** Optional visible legend; falls back to the accessible name. */
  legend?: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  options: RadioOption[];
  /** Story 8.13 (ADO-2022): an error message; when set the group renders its invalid state. */
  error?: ReactNode;
  /** Story 8.13 (ADO-2022): `horizontal` lays the options side by side, wrapping rather than
   *  widening a narrow screen. Vertical by default. */
  orientation?: 'vertical' | 'horizontal';
}

/** The group's accessible name: `label`, or (Story 8.13, ADO-2022) `labelledBy`, the id of a
 *  visible element that names it (a card heading). One or the other, never both, so a label is
 *  never silently dropped. */
export type RadioGroupProps = RadioGroupBaseProps &
  ({ label: string; labelledBy?: never } | { labelledBy: string; label?: never });

export function RadioGroup({
  label,
  legend,
  name,
  value,
  onChange,
  options,
  error,
  orientation = 'vertical',
  labelledBy,
}: RadioGroupProps) {
  const reactId = useId();
  const groupName = name ?? reactId;
  const errorId = error ? `${reactId}-error` : undefined;

  return (
    <div
      role="radiogroup"
      {...(labelledBy === undefined ? { 'aria-label': label } : { 'aria-labelledby': labelledBy })}
      aria-orientation={orientation}
      data-orientation={orientation}
      aria-invalid={error ? true : undefined}
      aria-describedby={errorId}
      className={styles.group}
    >
      {legend ? <span className={styles.label}>{legend}</span> : null}
      {options.map((opt) => (
        <label key={opt.value} className={styles.option}>
          <input
            type="radio"
            className={styles.input}
            name={groupName}
            value={opt.value}
            checked={value === opt.value}
            disabled={opt.disabled}
            onChange={() => onChange(opt.value)}
          />
          <span className={styles.label}>{opt.label}</span>
        </label>
      ))}
      {error ? (
        <span id={errorId} className={styles.error} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
